import Point from "src/dataTypes/geometry/Point";
import Polygon from "src/dataTypes/geometry/Polygon";
import Rectangle from "src/dataTypes/geometry/Rectangle";
import NumberOperators from "src/operators/numeric/NumberOperators";
import PolygonOperators from 'src/operators/geometry/PolygonOperators';

/**
 * @classdesc Functions to create Polygons from a set of points
 *
 * @namespace
 * @category geometry
 */
function PolygonGenerators() {}
export default PolygonGenerators;

/**
 * Generate a list of Points
 * @param  {number} nPoints number of points to generate
 *
 * @param  {number} mode to use for point generation<|>0: random(default)<|>1: grid<|>2: hex grid<|>3: grid with noise<|>4: random with voronoi relaxation
 * @param  {Rectangle} rFrame optional rectangle to use for the boundary of the points. If not specified the unit square is used.
 * @return {Polygon} polygon of positions for each list element. Points are in same order as input.
 * @param  {number} seed to use for random function to get repeatable results
 * tags:geometry
 */
PolygonGenerators.createPolygon = function(nPoints, mode, frame, seed) {
  if(nPoints == null || nPoints == 0) return;

  var polygon = new Polygon();
  frame = frame == null ? new Rectangle(0,0,1,1) : frame;
  mode = mode == null ? 0 : mode;
  if(seed) NumberOperators.randomSeed(seed);
  if(nPoints == 1){
    polygon.push(frame.getCenter());
    return polygon;
  }

  switch(mode) {
    case 0: //random
      for(let i = 0; i < nPoints; i++) {
        polygon.push(new Point(frame.x + frame.width * NumberOperators.random(), frame.y + frame.height * NumberOperators.random()));
      }
      break;
    case 1: //grid
    case 2: // hex grid
    case 3: // hex grid with noise
      let cols = Math.ceil(Math.sqrt(nPoints*Math.sqrt(frame.width/frame.height)));
      let rows = Math.ceil(nPoints/cols);
      let bx = Math.max(0.01,0.25/Math.pow(2,cols-2))*frame.width;
      let by = Math.max(0.01,0.25/Math.pow(2,rows-2))*frame.height;
      let w=(frame.width-2*bx)/(cols - (mode == 1 ? 1 : 0.5) );
      let h=(frame.height-2*by)/(rows-1);// good for modes 1-2 and 3 when c < limit
      let h1=(frame.height-2*by)/(rows-1.75);
      let fRand = mode > 2 ? .5 : 0;
      let nMissing = cols*rows-nPoints;
      let x,y,p;
      for(let c=0;c<cols;c++){
        for(let r=0;r<rows;r++){
          let i=r*cols+c;
          if(i >= nPoints) continue;
          x = bx+frame.x + c*w + ( (mode > 1 && r%2 == 1) ? w/2 : 0) + fRand*w*(.5-NumberOperators.random());
          let h2 = (mode != 3 || c < cols - nMissing) ? h : h1;
          y = by+frame.y + r*h2 + fRand*h2*(.5-NumberOperators.random());
          p = mode == 3 ? frame.constrainPoint(new Point(x,y)) : new Point(x,y);
          polygon.push(p);
        }
      }
      polygon = polygon.getSortedRandom();
      break;
    case 4: //random with relaxation
      // if rectangles are really skinny then sometimes the voronoi process has trouble
      let hFact = 1;
      frame = frame.clone();
      if(frame.width / frame.height > 40){
        hFact = frame.width / frame.height;
        frame.y *= hFact;
        frame.height *= hFact;
      }
      else if(frame.width / frame.height < 1/40){
        hFact = frame.width / frame.height;
        frame.y *= hFact;
        frame.height *= hFact;
      }
      polygon = PolygonGenerators.createPolygon(nPoints,0,frame);
      let vor;
      for(let i=0;i<2;i++){
        try{
          vor = PolygonOperators.voronoi(polygon,frame);
        }
        catch(e){
          console.log(e);
          break;
        }
        for(let j=0;j<vor.length;j++){
          let pc = vor[j].getBarycenter();
          polygon[j].x = pc.x;
          polygon[j].y = pc.y / (i==1 ? hFact : 1);
        }
      }
      // the voronoi process depends somewhat on point order so we randomize order of indices also
      polygon = polygon.getSortedRandom();
      break;
  }

  if(seed) NumberOperators.randomSeedPop();

  return polygon;
};

/**
 * For a list with categorical values create a polygon of points spread across a grid, one location for each category
 * @param  {List} list of categorical elements
 *
 * @param  {Rectangle} rFrame optional rectangle to use for the grid. If not specified the unit square is used.
 * @return {Polygon} polygon of positions for each list element. Points are in same order as input.
 * tags:geometry
 */
PolygonGenerators.createPolygonForCategoricalList = function(list, rFrame) {
  if(list == null) return;
  rFrame = rFrame == null ? new Rectangle(0,0,1,1) : rFrame;
  var pRet = new Polygon();
  if(list.length == null || list.length == 0) return pRet;
  var list2 = list.getWithoutRepetitions().getSorted();
  var n = list2.length;
  var rows = Math.ceil(Math.sqrt(n*rFrame.height/rFrame.width));
  var cols = Math.ceil(rows*rFrame.width/rFrame.height);
  var colWidth = rFrame.width/(cols-1);
  var rowHeight = rFrame.height/(rows-1);
  var pGrid = new Polygon();
  for(var r=0; r < rows; r++){
    for(var c=0; c < cols; c++){
      pGrid.push(new Point(rFrame.x + c*colWidth,rFrame.y + r*rowHeight));
    }
  }
  for(var i=0;i < list.length; i++){
    var i2 = list2.indexOfElement(list[i]);
    pRet.push(pGrid[i2].clone());
  }
  return pRet;
};


// PolygonGenerators.getCirclesDisposedInSpiral=function(weights, frame){ //TODO: this method belongs to another class (?)
// 	var sortedCenters;
// 	var centers = new Polygon();
// 	var sortedRadius;
// 	var radius = new NumberList();
// 	var sortArray = weights.sortNumericIndexedDescending();
// 	//trace("sortArray:", weights, sortArray);
// 	var maxWeight = 0;
// 	var nElements = weights.length;
// 	var i;
// 	var j;

// 	if(nElements==1){
// 		var table=new Table();
// 		var pointList=new Polygon();
// 		var point=new Point()
// 		pointList.push(point);
// 		table.push(pointList);
// 		var list=new List();
// 		list.push(1);
// 		table.push(list);
// 		return table;
// 	}
// 	maxWeight =weights.getMax();


// 	if(maxWeight==0) return null;

// 	var MIN_SPACE_BETWEEN_CIRCLES = 0.1;

// 	sortedRadius = new NumberList(Math.sqrt(weights[sortArray[0]]/maxWeight), Math.sqrt(weights[sortArray[1]]/maxWeight));
// 	sortedCenters = new Polygon(new Point(0,0), new Point(sortedRadius[0] + sortedRadius[1] + MIN_SPACE_BETWEEN_CIRCLES,0));
// 	//trace("sortedCenters:", sortedCenters),
// 	centers[sortArray[0]] = sortedCenters[0];
// 	radius[sortArray[0]] = sortedRadius[0];

// 	centers[sortArray[1]] = sortedCenters[1];
// 	radius[sortArray[1]] = sortedRadius[1];
// 	//trace(centers);
// 	//trace(radius);
// 	var r;
// 	var rI;
// 	var angle = 0;

// 	var testPoint = new Point(0, 0);
// 	var externR = sortedCenters[1].x + sortedRadius[1];

// 	//var ACCUM_J:Number=0;

// 	for(i=2; i<nElements; i++){
// 		rI = Math.sqrt(weights[sortArray[i]]/maxWeight);
// 		//trace(i, "rI", rI);
// 		r = sortedRadius[0] + rI + MIN_SPACE_BETWEEN_CIRCLES;
// 		angle = i;

// 		for(j=0; j<100000; j++){
// 			testPoint.x = r*Math.cos(angle);
// 			testPoint.y = r*Math.sin(angle);

// 			r+=0.01;
// 			angle+=r*0.04;

// 			if(Polygon.testCircleAtPoint(testPoint, rI+MIN_SPACE_BETWEEN_CIRCLES, sortedCenters, sortedRadius) || j==99999){
// 				sortedCenters.push(new Point(testPoint.x, testPoint.y));
// 				sortedRadius.push(rI);
// 				centers[sortArray[i]] = sortedCenters[i];
// 				radius[sortArray[i]] = sortedRadius[i];
// 				externR = Math.max(externR, Math.sqrt(Math.pow(testPoint.x, 2)+Math.pow(testPoint.y, 2)) + rI);
// 				break;
// 			}
// 		}
// 		//ACCUM_J+=j

// 	}
// 	//trace("   packingCircles:ACCUM_J", ACCUM_J);
// 	//trace("c:", centers);
// 	//trace("r:", radius);
// 	var mulVal=1/externR;
// 	for(i=0; i<centers.length; i++){
// 		centers[i].x*=mulVal;
// 		centers[i].y*=mulVal;
// 	}
// 	for(i=0; i<radius.length; i++){
// 		radius[i]*=mulVal;
// 	}
// 	//centers = centers.multiply();
// 	//radius = radius.multiply(1/externR);
// 	//trace("c2:", centers);
// 	//trace("r2:", radius);

// 	return new Array(centers, radius);
// }
